import { Switch } from 'antd'
import { BpTooltip } from '../../../../shared/components/cards/tooltip/BpTooltip'
import React, { useMemo } from 'react'
import { AiButton } from '../../../../shared/components/ai/ai-button/AiButton'
import { Trans, useTranslation } from 'react-i18next'
import { useAiChat } from '../../../../shared/components/ai/ai-provider/AiProvider'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { IconMinimize } from '../../../../shared/components/icons/IconMinimize'
import { IconMaximize } from '../../../../shared/components/icons/IconMaximize'
import { ExportDrop } from '../../../../shared/components/cards/export-drop/ExportDrop'
import { useAppDispatch, useAppSelector } from '../../../../app/store/hooks/Redux'
import {
  selectTrademarkInfringementsExpandAllRows,
  selectTrademarkInfringementsFilters,
  selectTrademarkInfringementsPagination,
  selectTrademarkTabCount,
  setExpandedRows,
  setTrademarkInfringementsFilters,
  setTrademarkInfringementsOnlyRegistered,
} from '../../model/trademarkInfringementsSlice'
import { ITrademarkInfringementsFilters } from '../../model/types/TrademarkInfringementsFilters.interface'
import { ExportTmInfringementsArg, useExportTmInfringements } from '../../../../app/api'
import { AlertingChip } from '../../../../lib/widgets/alerting-chip/AlertingChip'

export const TrademarkInfringementsToolbar = () => {
  const { t } = useTranslation()
  const { handleSendMessage } = useAiChat()
  const { tab, onlyRegistered, status, ...filters } =
    useAppSelector<ITrademarkInfringementsFilters>(selectTrademarkInfringementsFilters)
  const { current, pageSize } = useAppSelector(selectTrademarkInfringementsPagination)
  const expandAllRows = useAppSelector(selectTrademarkInfringementsExpandAllRows)
  const tabCount = useAppSelector(selectTrademarkTabCount)
  const dispatch = useAppDispatch()

  const [exportFile, { isFetching: isLoading }] = useExportTmInfringements()

  const queryParams = useMemo(
    () => ({
      page: current,
      'per-page': pageSize,
      projectId: filters?.project,
      countryId: filters?.country,
      site: filters?.site,
      date: filters.dateRange,
      trademarkId: filters?.trademark,
      caseId: filters?.caseId,
      showWhitelisted: filters?.showWhitelisted,
      tab: tab,
      onlyRegistered: onlyRegistered,
      ...(tab === 'in_progress' && { status: status }),
    }),
    [current, pageSize, tab, onlyRegistered, status, filters],
  )

  const statuses = [
    { text: t('pages.trademarkInfringements.submitted'), value: 'submitted' },
    { text: t('pages.trademarkInfringements.accepted'), value: 'accepted' },
    { text: t('pages.trademarkInfringements.rejected'), value: 'rejected' },
  ] as const

  return (
    <div className={'flex flex-col justify-between gap-[8px] lg:flex-row lg:items-center'}>
      {
        {
          to_submit: (
            <div className={'flex items-center gap-[8px]'}>
              <Switch
                defaultChecked={onlyRegistered}
                onChange={(e) => dispatch(setTrademarkInfringementsOnlyRegistered(e))}
              />
              <span>{t('pages.trademarkInfringements.dataForRegisteredTrademarks')}</span>
              <BpTooltip title={t('pages.trademarkInfringements.onlyRegisteredTooltip')} />
            </div>
          ),
          in_progress: (
            <div className={'flex items-center gap-[8px]'}>
              <button
                className={`whitespace-nowrap rounded-full px-[12px] py-[4px] ${!status ? 'bg-default text-white' : 'bg-focus/[0.05] text-focus-500 hover:bg-focus/[0.1]'}`}
                onClick={() =>
                  dispatch(
                    setTrademarkInfringementsFilters({
                      tab,
                      onlyRegistered,
                      status: undefined,
                      ...filters,
                    }),
                  )
                }
              >
                {t('pages.trademarkInfringements.all')}{' '}
                <span className={!status ? 'text-white' : 'text-[#8E7CA7]'}>
                  {tabCount.submitted + tabCount.accepted + tabCount.rejected}
                </span>
              </button>
              {statuses.map(({ text, value }) => {
                const isActive = status === value

                return (
                  <button
                    key={value}
                    className={`whitespace-nowrap rounded-full px-[12px] py-[4px] ${isActive ? 'bg-default text-white' : 'bg-focus/[0.05] text-focus-500 hover:bg-focus/[0.1]'}`}
                    onClick={() =>
                      dispatch(
                        setTrademarkInfringementsFilters({
                          tab,
                          onlyRegistered,
                          status: value,
                          ...filters,
                        }),
                      )
                    }
                  >
                    {text}{' '}
                    <span className={isActive ? 'text-white' : 'text-[#8E7CA7]'}>
                      {tabCount[value]}
                    </span>
                  </button>
                )
              })}
            </div>
          ),
          removed: (
            <p className={'text-[#00326D]'}>
              <Trans i18nKey='pages.trademarkInfringements.seeImpactYourSubmissions'>
                <a
                  className={'text-focus'}
                  href='https://support.google.com/adspolicy/answer/6118?hl=en'
                  rel='noreferrer'
                  target={'_blank'}
                />
              </Trans>
            </p>
          ),
        }[tab]
      }
      <div className={'flex items-center justify-between gap-[24px] lg:ml-auto'}>
        {
          {
            bluepear: (
              <AiButton
                unstyled
                onClick={() =>
                  handleSendMessage({
                    message:
                      'Please provide a clear guide on how the Trademark Complaint Submission tool works and explain its value.',
                  })
                }
              >
                <span className={'whitespace-nowrap'}>
                  {t('pages.trademarkInfringements.howComplaintSubmissionWorks')}
                </span>
              </AiButton>
            ),
            robuscan: null,
            default: null,
          }[process.env.REACT_APP_PROJECT ?? 'default']
        }
        <div className={'flex items-center gap-[8px]'}>
          <BpButton
            iconLeft={expandAllRows ? <IconMinimize /> : <IconMaximize />}
            size='sm'
            theme='outline'
            type='button'
            onClick={() => dispatch(setExpandedRows(expandAllRows ? [] : 'all'))}
          ></BpButton>
          <AlertingChip
            projectId={Number(filters.project) || undefined}
            type={'tm_infringements'}
          />
          <ExportDrop
            columns={[
              { label: t('table.columns.domain'), keys: ['domain'] },
              { label: t('table.columns.country'), keys: ['country'] },
              { label: t('table.columns.ads'), keys: ['ads_count'] },
              { label: t('table.columns.trademark'), keys: ['trademark.name'] },
              { label: t('table.columns.latestAdExample'), keys: ['latest_ad'] },
              { label: t('table.columns.lastFound'), keys: ['last_found'] },
              { label: t('table.columns.latestComplaint'), keys: ['complaint_status'] },
            ]}
            loading={isLoading}
            onExport={(exportParams) =>
              exportFile({ ...queryParams, ...exportParams } as unknown as ExportTmInfringementsArg)
            }
          />
        </div>
      </div>
    </div>
  )
}
